import { defaultJobDraftV2, OrderStateV2 } from './context';

// Pure reducers for the two orchestrator setState callbacks that re-check
// race-prone guards inside the updater. Extracted from `index.tsx` so they
// can be unit-tested independently of the hook implementation, and so a
// future React Query swap of `useCertificates` / `useRateQuote` cannot
// silently regress the "don't clobber template prefill" or "don't clobber
// user-entered rates" behavior.

// Auto-seed the customer's default certificate ids onto `jobs[0].certificates`
// — but only when the wizard wasn't prefilled from a template AND no
// certificates have been picked yet. Mirrors the legacy behavior of the
// `useCertificates` callback inside the orchestrator.
export const autoSeedCertificateDefaultsReducer = (
  prev: OrderStateV2,
  defaultIds: number[],
): OrderStateV2 => {
  if (prev.isPrefilled) return prev;
  if (prev.jobs.length === 0) return prev;
  const job = prev.jobs[0];
  if ((job.certificates || []).length > 0) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = { ...job, certificates: defaultIds };
  return { ...prev, jobs: nextJobs };
};

// Start a new skill from the Review step. Prepends a fresh empty draft
// to `jobs[0]` so the wizard (which always reads/writes `jobs[0]`) edits
// the new skill while previously completed jobs are preserved at indices
// 1+. Mirrors the v1 `resetState() + ADD_JOB` split — without this, the
// wizard re-edits and overwrites the original `jobs[0]` instead of
// appending a separate job entry to the order.
export const addNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => ({
  ...prev,
  jobs: [defaultJobDraftV2(prev.orderType || 'GIG'), ...prev.jobs],
});

// Cancel an in-progress new skill draft and fall back to the previously
// completed job(s). No-op when `jobs.length <= 1` so a single-skill order
// can't accidentally drop its only job — the wizard's existing "Cancel"
// button still handles that case by leaving the wizard altogether.
export const cancelNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (prev.jobs.length <= 1) return prev;
  return { ...prev, jobs: prev.jobs.slice(1) };
};

// Apply a fresh rate quote to `jobs[0]` — but only when the user hasn't
// already entered a pay/cost rate. Background refetches must never
// overwrite manual edits. Mirrors the legacy `useRateQuote` callback.
export const applyRateQuoteReducer = (
  prev: OrderStateV2,
  pay: number,
  cost: number,
): OrderStateV2 => {
  const job = prev.jobs[0];
  if (!job) return prev;
  if (job.pay_rate || job.cost_rate) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = {
    ...job,
    pay_rate: pay,
    cost_rate: cost,
    original_pay_rate: pay,
    original_cost_rate: cost,
  };
  return { ...prev, jobs: nextJobs };
};
